import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Initial state
const initialState = {
    billedJobsList: [],
    billedJobsLoading: false,
    billedJobsError: null,
    selectedBilledJob: null,
    selectedBilledJobLoading: false,
    selectedBilledJobError: null,
    totalResults: 0,
    filterOptions: {
        draw: 1,
        start: 0,
        length: 10,
        noBilledJobs: false, // false = mostro solo le commesse fatturate
        jobCode: '',
        jobStatusId: '',
        jobTypeId: '',
        customerId: '',
        keywords: ''
    }
};

// Async actions
export const getBilledJobsList = createAsyncThunk(
    "billedJobs/getBilledJobsList",
    async ({ accountId, token, filterOptions }, { rejectWithValue }) => {
        try {
            const params = {
                accountId,
                noBilledJobs: false, // Importante: false indica di mostrare solo le commesse fatturate
                ...filterOptions
            };
            
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL || ''}/jobs/list`, {
                headers: {
                    "auth-token": token
                },
                params
            });
            
            return {
                data: response.data.data,
                totalResults: response.data.recordsTotal || response.data.data.length
            };
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nel recupero delle commesse fatturate");
        }
    }
);

export const getBilledJobDetail = createAsyncThunk(
    "billedJobs/getBilledJobDetail",
    async ({ jobId, token }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL || ''}/jobs/`, {
                headers: {
                    "auth-token": token
                },
                params: {
                    id: jobId
                }
            });
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nel recupero dei dettagli della commessa");
        }
    }
);

// Slice
const billedJobsSlice = createSlice({
    name: "billedJobs",
    initialState,
    reducers: {
        resetSelectedBilledJob: (state) => {
            state.selectedBilledJob = null;
            state.selectedBilledJobError = null;
        },
        updateFilterOptions: (state, action) => {
            state.filterOptions = {
                ...state.filterOptions,
                ...action.payload
            };
        },
        resetFilters: (state) => {
            state.filterOptions = {
                ...initialState.filterOptions,
                draw: state.filterOptions.draw + 1 // Incrementa il contatore draw
            };
        }
    },
    extraReducers: (builder) => {
        builder
            // Gestione getBilledJobsList
            .addCase(getBilledJobsList.pending, (state) => {
                state.billedJobsLoading = true;
                state.billedJobsError = null;
            })
            .addCase(getBilledJobsList.fulfilled, (state, action) => {
                state.billedJobsLoading = false;
                state.billedJobsList = action.payload.data;
                state.totalResults = action.payload.totalResults;
            })
            .addCase(getBilledJobsList.rejected, (state, action) => {
                state.billedJobsLoading = false;
                state.billedJobsError = action.payload;
            })
            
            // Gestione getBilledJobDetail
            .addCase(getBilledJobDetail.pending, (state) => {
                state.selectedBilledJobLoading = true;
                state.selectedBilledJobError = null;
            })
            .addCase(getBilledJobDetail.fulfilled, (state, action) => {
                state.selectedBilledJobLoading = false;
                state.selectedBilledJob = action.payload;
            })
            .addCase(getBilledJobDetail.rejected, (state, action) => {
                state.selectedBilledJobLoading = false;
                state.selectedBilledJobError = action.payload;
            });
    }
});

// Export actions
export const { 
    resetSelectedBilledJob,
    updateFilterOptions,
    resetFilters
} = billedJobsSlice.actions;

// Export selectors
export const selectBilledJobsList = (state) => state.billedJobs.billedJobsList;
export const selectBilledJobsLoading = (state) => state.billedJobs.billedJobsLoading;
export const selectBilledJobsError = (state) => state.billedJobs.billedJobsError;
export const selectSelectedBilledJob = (state) => state.billedJobs.selectedBilledJob;
export const selectSelectedBilledJobLoading = (state) => state.billedJobs.selectedBilledJobLoading;
export const selectSelectedBilledJobError = (state) => state.billedJobs.selectedBilledJobError;
export const selectTotalResults = (state) => state.billedJobs.totalResults;
export const selectFilterOptions = (state) => state.billedJobs.filterOptions;

// Export reducer
export default billedJobsSlice.reducer;
